import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAdminAuth } from "@/lib/adminAuth";
import Icon from "@/components/Icon";

const NAV = [
  { to: "/admin", label: "لوحة المعلومات", icon: "sparkles", end: true },
  { to: "/admin/quote-requests", label: "طلبات العروض", icon: "send" },
  { to: "/admin/products", label: "المنتجات", icon: "boxes" },
  { to: "/admin/categories", label: "الفئات", icon: "boxes" },
  { to: "/admin/services", label: "الخدمات", icon: "shield" },
  { to: "/admin/projects", label: "المشاريع", icon: "sparkles" },
  { to: "/admin/testimonials", label: "آراء العملاء", icon: "star" },
  { to: "/admin/settings", label: "الإعدادات", icon: "wrench" },
  { to: "/admin/backups", label: "النسخ الاحتياطي", icon: "shield-check" },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const doLogout = () => {
    logout();
    toast.success("تم تسجيل الخروج.");
    nav("/admin/login");
  };

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {/* Sidebar (RTL: on the right side) */}
      <aside
        className={`fixed lg:sticky top-0 h-screen z-40 w-72 bg-[#0A1628] text-white flex-col
          ${open ? "flex" : "hidden lg:flex"}`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#00b8cc] flex items-center justify-center">
              <Icon name="shield-check" className="text-[#0A1628]" size={22} strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-heading text-xl font-bold">أمان</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300/80">Admin Panel</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#00E5FF] text-[#0A1628]"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
              data-testid={`admin-nav-${n.to.replace("/admin", "").replace("/", "") || "dashboard"}`}
            >
              <Icon name={n.icon} size={18} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2">مسجّل الدخول كـ</div>
          <div className="text-sm text-white font-semibold mb-3">{user?.username}</div>
          <div className="flex gap-2">
            <Link
              to="/"
              className="flex-1 text-center rounded-lg border border-white/10 py-2 text-slate-300 hover:text-white text-xs transition-colors"
              target="_blank"
              rel="noreferrer"
            >
              عرض الموقع
            </Link>
            <button
              onClick={doLogout}
              className="flex-1 rounded-lg bg-red-500/10 border border-red-500/30 py-2 text-red-300 hover:bg-red-500/20 text-xs transition-colors"
              data-testid="admin-logout"
            >
              خروج
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-lg text-[#0A1628]"
            aria-label="menu"
            data-testid="admin-mobile-toggle"
          >
            <Icon name={open ? "x" : "menu"} size={22} />
          </button>
          <div className="font-heading font-bold text-[#0A1628]">لوحة التحكم</div>
          <div className="w-8" />
        </div>

        <main className="p-6 md:p-10 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
